import React from 'react';
import { CheckCircle2, Clock, XCircle, Circle, ChevronRight, GitBranch } from 'lucide-react';

/**
 * Horizontal journey visualization.
 *
 * Shows the canonical happy-path stages as a scrolling row of nodes with animated
 * connectors, plus a "branch" node (rejection) that fans off from the current stage
 * whenever the final_outcome is not yet decided.
 *
 * Renders identically for recruiter + applicant — no role-specific writes.
 *
 * Props:
 *   journey: {
 *     stages: [{ key, label, done, current, entered_at? }],
 *     current_stage: 'matched'|...|'offer_signed',
 *     final_outcome: 'selected'|'rejected'|null,
 *   }
 */
export default function JourneyHorizontal({ journey }) {
  if (!journey || !Array.isArray(journey.stages)) return null;

  const finalRejected = journey.final_outcome === 'rejected';
  const finalSelected = journey.final_outcome === 'selected';
  const stages = journey.stages;
  // For rejected journeys, `current` flags are cleared server-side; fall back to the last done stage.
  const activeIdx = stages.findIndex((s) => s.current);
  const lastDoneIdx = stages.reduce((acc, s, i) => (s.done ? i : acc), -1);
  const currentIdx = activeIdx >= 0 ? activeIdx : lastDoneIdx;

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-2xl p-5 mb-4" data-testid="journey-horizontal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm uppercase tracking-widest text-slate-500 dark:text-zinc-500 flex items-center gap-2">
          <GitBranch className="w-4 h-4" /> Journey map
        </h2>
        <p className="text-[11px] text-slate-400 dark:text-zinc-500">
          {finalSelected ? 'Complete · Offer signed' : finalRejected ? 'Ended · Not selected' : `Stage ${currentIdx + 1} of ${stages.length}`}
        </p>
      </div>

      <div className="relative">
        {/* Horizontal scrolling track */}
        <div className="overflow-x-auto pb-3 -mx-1 px-1 [scrollbar-width:thin]" data-testid="journey-horizontal-track">
          <div className="inline-flex items-stretch gap-0 min-w-full">
            {stages.map((s, i) => {
              const isLast = i === stages.length - 1;
              const stoppedHere = finalRejected && i === currentIdx;
              return (
                <React.Fragment key={s.key}>
                  <StageNode stage={s} stoppedHere={stoppedHere} finalSelected={finalSelected} />
                  {!isLast && (
                    <Connector done={s.done && stages[i + 1].done} current={s.current} rejected={finalRejected && i >= currentIdx} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Rejection branch line removed — it was misleading to candidates. */}
        {finalRejected && (
          <div className="mt-3 pl-4 border-l-2 border-red-300 dark:border-red-800 ml-2">
            <p className="text-[11.5px] text-red-600 dark:text-red-400 flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5" /> Ended at &ldquo;{stages[currentIdx]?.label || stages[currentIdx]?.key || 'the current stage'}&rdquo;
            </p>
            {journey.rejection_reason && (
              <p className="text-[11px] text-red-500 dark:text-red-400/90 mt-0.5 italic">
                Reason: {journey.rejection_reason}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StageNode({ stage, stoppedHere, finalSelected }) {
  const done = stage.done && !stoppedHere;
  const current = stage.current;
  const isFinalSigned = finalSelected && stage.key === 'offer_signed';

  let bubbleCls = 'bg-slate-100 dark:bg-zinc-900 text-slate-500 dark:text-zinc-500 border-slate-200 dark:border-zinc-800';
  let labelCls = 'text-slate-500 dark:text-zinc-500';
  let Icon = Circle;
  let iconCls = 'w-4 h-4';

  if (stoppedHere) {
    bubbleCls = 'bg-red-100 dark:bg-red-950 text-red-600 dark:text-red-400 border-red-300 dark:border-red-800 ring-4 ring-red-100 dark:ring-red-950/40';
    labelCls = 'text-red-700 dark:text-red-300';
    Icon = XCircle;
  } else if (isFinalSigned) {
    bubbleCls = 'bg-emerald-200 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-200 border-emerald-400 dark:border-emerald-700 ring-4 ring-emerald-100 dark:ring-emerald-950/40';
    labelCls = 'text-emerald-800 dark:text-emerald-200';
    Icon = CheckCircle2;
  } else if (done) {
    bubbleCls = 'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800';
    labelCls = 'text-slate-800 dark:text-zinc-200';
    Icon = CheckCircle2;
  } else if (current) {
    bubbleCls = 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-800 animate-pulse';
    labelCls = 'text-amber-700 dark:text-amber-300 font-medium';
    Icon = Clock;
  }

  return (
    <div className="flex flex-col items-center min-w-[104px] px-1" data-testid={`journey-h-node-${stage.key}`}>
      <div className={`w-11 h-11 rounded-full border-2 flex items-center justify-center shrink-0 transition ${bubbleCls}`}>
        <Icon className={iconCls} />
      </div>
      <p className={`text-[11.5px] mt-2 text-center leading-tight max-w-[100px] ${labelCls}`}>
        {stage.label}
      </p>
      {stage.entered_at && (
        <p className="text-[10px] text-slate-400 dark:text-zinc-500 mt-0.5">
          {new Date(stage.entered_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

function Connector({ done, rejected }) {
  const cls = rejected
    ? 'bg-red-200 dark:bg-red-900'
    : done
      ? 'bg-emerald-300 dark:bg-emerald-800'
      : 'bg-slate-200 dark:bg-zinc-800';
  return (
    <div className="flex items-center min-w-[36px] w-[36px] shrink-0 -mt-6 relative">
      <div className={`h-[3px] w-full rounded-full ${cls}`} />
      <ChevronRight
        className={`w-3 h-3 absolute right-[-4px] top-1/2 -translate-y-1/2 ${rejected ? 'text-red-400' : done ? 'text-emerald-500' : 'text-slate-400'}`}
      />
    </div>
  );
}
